import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { FiMail } from 'react-icons/fi';
import { ApexOptions } from 'apexcharts'; // Type definitions for ApexCharts

// Type casting for FiMail icon
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// StatBox component for displaying statistics
const StatBox: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) =>
  label !== 'New Subscribers' &&
    label !== 'Unsubscribes' &&
    label !== 'Last Opened' &&
    label !== 'Last Clicked' ? (
    // Standard stat box
    <div className='bg-white rounded-md p-3 flex items-center justify-between border border-gray-200 shadow-sm'>
      <div>
        <h3 className='text-[#657584] text-[14px] mb-2 font-semibold'>
          {label}
        </h3>
        <p className='text-[28px] text-[#3F5265] font-semibold'>{value}</p>
      </div>
      <div style={{ height: '-webkit-fill-available' }}>
        <span className='flex items-center justify-center mr-2 w-[30px] h-[30px] rounded-[5px] bg-[#FFF1EC]'>
          <FiMailComponent className='text-orange-500 text-2xl' size={17} />
        </span>
      </div>
    </div>
  ) : (
    // Modified stat box for specific metrics
    <div
      className={`bg-white ${label === 'New Subscribers' ? 'rounded-t' : 'rounded-b'
        } p-[13.5px] flex items-center justify-between border border-gray-200 shadow-sm`}
    >
      <div className='flex justify-between items-center w-[100%]'>
        <h3 className='text-[#18181B] text-[14px] font-semibold'>{label}</h3>
        <p className='text-[20px] text-[#3F5265] font-semibold'>{value}</p>
      </div>
    </div>
  );

// Interface for automation data
// interface Automation {
//   id: string;
//   name: string;
//   type: string;
//   status: string;
//   recipients: string;
//   openRate: string;
//   clickRate: string;
//   triggerDate: string;
//   action: string;
//   subtitle?: string;
//   segment?: string;
//   tags?: string[];
//   deliveredDate?: string;
//   stats?: {
//     sent: number;
//     delivered: number;
//     opened: number;
//     clicked: number;
//     bounced: number;
//     spamReported: number;
//     openRate: string;
//     clickRate: string;
//     newSubscribers: number;
//     unsubscribes: number;
//   };
//   lastOpened?: { name: string; time: string };
//   lastClicked?: { name: string; time: string };
// }

interface OverviewProps {
  automation: any;
}

// Main component for displaying automation overview with stats and chart
const ReportsAutomationOverview: React.FC<OverviewProps> = ({ automation }) => {
  // State for time filter (24h, 7d, 30d)
  const [filter, setFilter] = useState<'24h' | '7d' | '30d'>('24h');

  // Handle case where no stats are available
  if (!automation.stats) {
    return (
      <div className='bg-white p-4 rounded-lg shadow border'>
        <h3 className='text-[20px] font-semibold text-[#262626] mb-2'>
          Engagement
        </h3>
        <p className='text-gray-500 text-sm'>No stats available yet.</p>
      </div>
    );
  }

  // Sample engagement data for different time periods
  const engagementData30d = [
    { name: 'Welcome Email', opened: 12000, clicked: 2500 },
    { name: 'Product Introduction Email', opened: 9500, clicked: 1800 },
    { name: 'Special Offer Email', opened: 7000, clicked: 1400 },
    { name: 'Final Reminder Email', opened: 5000, clicked: 1000 },
  ];
  const engagementData7d = [
    { name: 'Welcome Email', opened: 4200, clicked: 900 },
    { name: 'Product Introduction Email', opened: 3600, clicked: 750 },
    { name: 'Final Email', opened: 2800, clicked: 600 },
  ];
  const engagementData24h = [
    { name: 'Welcome Email', opened: 1200, clicked: 300 },
    { name: 'Product Introduction Email', opened: 950, clicked: 220 },
    { name: 'Final Email', opened: 700, clicked: 150 },
  ];

  // Select data based on filter
  const engagementData =
    filter === '30d'
      ? engagementData30d
      : filter === '7d'
        ? engagementData7d
        : engagementData24h;

  const series = [
    { name: 'Opened', data: engagementData.map((d) => d.opened) },
    { name: 'Clicked', data: engagementData.map((d) => d.clicked) },
  ];

  // ApexCharts configuration
  const options: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    xaxis: {
      categories: engagementData.map((d) => d.name),
      labels: { style: { fontSize: '12px', colors: '#657584' } },
    },
    yaxis: { labels: { style: { fontSize: '12px', colors: '#657584' } } },
    colors: ['#00899E', '#52CCB866'],
    legend: { position: 'top', horizontalAlign: 'left' },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        borderRadius: 4,
      },
    },
    grid: { borderColor: '#fff' },
  };

  return (
    <>
      {/* Stats Grid */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
        <StatBox label='Subscribed' value={automation.stats.sent} />
        <StatBox label='Conversion' value={automation.stats.delivered} />
        <StatBox label='Revenue Generated' value={automation.stats.opened} />
        <StatBox label='Engagement Score' value={automation.stats.clicked} />
      </div>

      {/* Engagement Chart */}
      <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
        <div className='flex justify-between items-center relative'>
          <select
            className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] absolute z-10 top-0 right-0'
            value={filter}
            onChange={(e) => setFilter(e.target.value as '24h' | '7d' | '30d')}
          >
            <option value='30d'>Last 30 Days</option>
            <option value='7d'>Last 7 Days</option>
            <option value='24h'>Last 24 Hours</option>
          </select>
        </div>
        <Chart options={options} series={series} type='bar' height={300} />
      </div>
    </>
  );
};

export default ReportsAutomationOverview;
